import { useState, useEffect } from 'react';
import { adminApi } from '@/services/admin';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import toast from 'react-hot-toast';
import { Loader2, Save, Image, Type } from 'lucide-react';

interface Branding {
  id: number;
  appName: string;
  logoUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BrandingSectionProps {
  superAdminToken: string | null;
}

export const BrandingSection = ({ superAdminToken }: BrandingSectionProps) => {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [appName, setAppName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchBranding();
  }, [superAdminToken]);

  const fetchBranding = async () => {
    if (!superAdminToken) return;

    try {
      setLoading(true);
      const response = await adminApi.getBranding(superAdminToken);
      setBranding(response.branding);
      setAppName(response.branding.appName);
      setLogoUrl(response.branding.logoUrl || '');
      setHasChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Nie udało się pobrać brandingu');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: 'appName' | 'logoUrl', value: string) => {
    if (field === 'appName') {
      setAppName(value);
      setHasChanges(value !== branding?.appName);
    } else {
      setLogoUrl(value);
      setHasChanges(value !== (branding?.logoUrl || ''));
    }
  };

  const handleSave = async () => {
    if (!superAdminToken) {
      toast.error('Brak tokenu super admin');
      return;
    }

    if (!appName.trim()) {
      toast.error('Nazwa aplikacji nie może być pusta');
      return;
    }

    try {
      setSaving(true);
      const response = await adminApi.updateBranding(superAdminToken, {
        appName: appName.trim(),
        logoUrl: logoUrl.trim() || null,
      });
      setBranding(response.branding);
      setHasChanges(false);
      toast.success('Branding został zaktualizowany!');
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Nie udało się zaktualizować brandingu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" />
            Nazwa aplikacji
          </CardTitle>
          <CardDescription>
            Nazwa wyświetlana w nagłówku i lewym menu aplikacji
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="appName">Nazwa aplikacji</Label>
            <Input
              id="appName"
              value={appName}
              onChange={(e) => handleInputChange('appName', e.target.value)}
              placeholder="CRM Meeting Notes"
              className="mt-2"
            />
          </div>

          {branding && (
            <div className="text-xs text-muted-foreground pt-2 border-t">
              Ostatnia aktualizacja: {new Date(branding.updatedAt).toLocaleString('pl-PL')}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="h-5 w-5" />
            Logotyp
          </CardTitle>
          <CardDescription>
            URL do logotypu wyświetlanego w lewym menu (zalecany rozmiar: 40x40px lub SVG)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="logoUrl">URL logotypu</Label>
            <Input
              id="logoUrl"
              value={logoUrl}
              onChange={(e) => handleInputChange('logoUrl', e.target.value)}
              placeholder="https://example.com/logo.svg"
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Możesz użyć zewnętrznego URL (np. z CDN) lub wgrać logo do katalogu /uploads
            </p>
          </div>

          {logoUrl && (
            <div>
              <Label>Podgląd</Label>
              <div className="mt-2 p-4 bg-muted rounded-lg inline-block">
                <img
                  src={logoUrl}
                  alt="Logo preview"
                  className="h-10 w-10 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = '';
                    e.currentTarget.alt = 'Nie można załadować logo';
                  }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-4 items-center">
        <div className="text-sm text-muted-foreground">
          {hasChanges && '• Niezapisane zmiany'}
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Zapisywanie...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Zapisz zmiany
            </>
          )}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-muted/50">
        <CardHeader>
          <CardTitle className="text-base">💡 Wskazówki</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• Logo będzie automatycznie skalowane do 40px wysokości z zachowaniem proporcji</p>
          <p>• Zalecamy używanie SVG dla najlepszej jakości na różnych ekranach</p>
          <p>• Logo powinno być kwadratowe lub panoramiczne (max 3:1)</p>
          <p>• Zmiany są widoczne natychmiast po odświeżeniu strony</p>
        </CardContent>
      </Card>
    </div>
  );
};
